import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";

import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

export type LabelRequiredOptionalProps = {
    /**
     * Whether the field is required. If true, a red asterisk will be shown.
     * Otherwise, a gray "Optional" will be shown. Note that this prop does not
     * ensure the field is filled out; it is only for display purposes.
     */
    required?: boolean;
} & React.ComponentProps<typeof LabelMedium>;

/**
 * A field label which shows a red asterisk if the field is required, and a
 * gray "Optional" if it is not. The asterisk is accessible and will be read as
 * "required" by screen readers.
 *
 * This component as all the same props as `LabelMedium`, with the addition of
 * `required`.
 */
export function LabelRequiredOptional(
    props: LabelRequiredOptionalProps,
): React.ReactNode {
    const defaultProps = {
        tag: "label",
        ...props,
    };

    // `style` needs to be applied after the label's styles, so we must remove
    // it from defaultProps which gets set first
    delete defaultProps.style;

    return (
        <LabelMedium {...defaultProps} style={[styles.label, props.style]}>
            <span>
                {props.children}{" "}
                {props.required && (
                    <span
                        className={css(styles.required)}
                        aria-label={t`required`}
                    >
                        *
                    </span>
                )}
            </span>{" "}
            {!props.required && (
                <span className={css(styles.optional)}>({t`Optional`})</span>
            )}
        </LabelMedium>
    );
}

const styles = StyleSheet.create({
    label: {
        display: "flex",
        justifyContent: "space-between",
        marginBlockEnd: spacing.small_12,
    },
    required: {
        color: color.red,
    },
    optional: {
        color: color.fadedOffBlack64,
    },
});
